/**
 * 車両属性（車両タイプ・車種・荷台高など）の選択肢マスタ。#329
 *
 * 値は文字列のまま既存カラム（trucks.klass / shipment_orders.designated_truck_klasses など）に
 * 保存し続けるので、name が「既存データに書かれている文字列そのもの」になる。
 */

/** API のカテゴリ。「温度」はデザインには在るが trucks に対応するカラムが無いので対象外 */
export const VEHICLE_ATTRIBUTE_CATEGORIES = [
  'klass',
  'car_model',
  'loading_platform_height',
  'loading_platform_width',
  'loading_platform_length',
  'floor_specification',
  'features',
] as const;

export type VehicleAttributeCategory = (typeof VEHICLE_ATTRIBUTE_CATEGORIES)[number];

/**
 * カテゴリの標準表示名。#329 で決めた表記に全画面を揃えているので、
 * 「荷台高さ」「荷台長さ」ではなく「荷台高」「荷台長」にする。
 * 会社ごとの呼称変更は #331。
 */
export const VEHICLE_ATTRIBUTE_CATEGORY_LABELS: Record<VehicleAttributeCategory, string> = {
  klass: '車両タイプ',
  car_model: '車種',
  loading_platform_height: '荷台高',
  loading_platform_width: '荷台幅',
  loading_platform_length: '荷台長',
  floor_specification: '床仕様',
  features: '装置',
};

export type VehicleAttributeValueEntity = {
  id: number;
  name: string;
  /** 取込の正規化に使う別名 */
  aliases: string[];
  /** 無効にすると新規の選択肢から外れる（既存データは残る） */
  active: boolean;
  position: number;
  /**
   * 改名した既存データの書き換えジョブが走行中か（#329 P6）。
   * 本番 worst case で約 2.1 分。異常終了しても 6 時間で API 側が false に戻すので永久には凍らない
   */
  updating: boolean;
  /**
   * 仮車両の既定の最大積載重量(kg)。#329 P7
   * 車両タイプ(klass)以外のカテゴリでは常に null。
   * 会社が足したタイプも既定値が無い間は null で、初回だけ利用者が入力する
   */
  default_weight_kg: number | null;
  /** 仮車両の既定の最大積載体積(m3)。DB が integer なので小数は入らない */
  default_volume_m3: number | null;
};

/**
 * GET /api/v2/vehicle_attribute_values/:id/usage。改名ダイアログの警告に出す使用件数。
 *
 * 本番最大テナントで 3〜4 秒かかる重いエンドポイントなので一覧には載せない。
 * キーは API の serialize そのまま（shipmentOrders だけ camelCase）
 */
export type VehicleAttributeValueUsageEntity = {
  trucks: number;
  shipmentOrders: number;
  places: number;
};

/** GET /api/v2/vehicle_attribute_values。値が 0 件のカテゴリも空配列で必ず来る */
export type VehicleAttributeValuesByCategory =
  Record<VehicleAttributeCategory, VehicleAttributeValueEntity[]>;

/** POST / PATCH のリクエストボディ。position はサーバーが決めるので送らない */
export type VehicleAttributeValueRequest = {
  name: string;
  aliases: string[];
  active: boolean;
};
